import { Action } from 'redux';
import { SMCTicketing } from '../../../web/models/constants/getHelpConstants';
import { appContext } from '../../../web/models/getHelp/appContext';
import { EcsConfigurationResponse } from '../../../web/models/getHelp/ecsConfigurationResponse';
import { ExperienceMetaData } from '../../../web/models/getHelp/experienceConfigurations';
import { GetAuthTokenPurpose } from '../../../web/models/getHelp/getAuthTokenPurpose';
import { AuthTokenRequest, EmeraldSigninType, TokenType } from '../../../web/models/getHelp/getHelpAuthToken';
import { IdentityChangedAction } from '../../../web/models/getHelp/IdentityChangedAction';
import * as actionTypes from '../../models/actionTypes';
import { SET_NAVBAR_SETTINGS } from '../../models/actionTypes';
import { Environment, NavBarSettings, OutdatedDevicesAlertEnabledComponent, SourceApps, VsbUpsellAlertEnabledComponent } from '../../models/constants/constants';
import { ISessionData, IUITabViewInfo } from '../../models/reduxState';
import { EmeraldConfigRequestModel, EmeraldConfigResponseModel } from '../contactUs/models/configModels';
import { signInUserIntoClientApp } from '../contactUs/reduxUpdater/actions/modalitySectionActions';
import { buildAuthTokenRequest } from '../../../web/utils/getHelputils';

export function genericSingleAction<T>(actionName: string, propName?: string, property?: T): Action {
  return {
    type: actionName,
    [propName]: property,
  };
}

export const initTeamsSdk = () => {
  return genericSingleAction<string>(actionTypes.INITIALIZE_TEAMS_SDK);
};

export const setNavBarSettings = (navBarSettings: NavBarSettings) => {
  return genericSingleAction<NavBarSettings>(SET_NAVBAR_SETTINGS, 'navBarSettings', navBarSettings);
};

export const setTheme = (theme: string) => {
  return {
    type: actionTypes.SET_THEME,
    theme: theme,
  };
};

export const setWindowsUpdateAvailableForSA = (isWindowsUpdateAvailable: boolean) => {
  if(isWindowsUpdateAvailable){
    return {type: actionTypes.SHOW_WINDOWS_UPDATE_AVAILABLE_FOR_SA};
  }
  else{
    return {type: actionTypes.DISMISS_WINDOWS_UPDATE_AVAILABLE_FOR_SA};
  }
}

export const setEndOfLifeMessageForSA = (isEndOfLifeReached: boolean) => {
  if (isEndOfLifeReached) {
    return { type: actionTypes.SHOW_END_OF_LIFE_MESSAGE_FOR_SA };
  }
  else {
    return { type: actionTypes.DISMISS_END_OF_LIFE_MESSAGE_FOR_SA };
  }
}

export const setEndOfServiceMessageForWin10SA = (isEndOfServiceReached: boolean) => {
  if (isEndOfServiceReached) {
    return { type: actionTypes.SHOW_END_OF_SERVICE_MESSAGE_FOR_WIN10_SA };
  }
  else {
    return { type: actionTypes.DISMISS_END_OF_SERVICE_MESSAGE_FOR_WIN10_SA };
  }
}

export const setMarket = (market: string) => {
  return {
    type: actionTypes.SET_MARKET,
    market: market,
  };
};

export const addToHistory = (href: string) => {
  return {
    type: actionTypes.ADD_TO_HISTORY,
    href: href,
  };
};

export const popHistory = () => {
  return {
    type: actionTypes.POP_HISTORY,
  };
};

export const disableBackButton = (disabled: boolean) => {
  return {
    type: actionTypes.DISABLE_BACK_BUTTON,
    disabled: disabled,
  };
};

export const hideRequestCreationTab = (isHidden: boolean) => {
  return {
    type: actionTypes.HIDE_REQUEST_CREATION_TAB,
    isHidden: isHidden,
  };
};

export const getEmeraldConfig = (request: EmeraldConfigRequestModel, environment: Environment) => {
  return {
    type: actionTypes.GET_EMERALD_CONFIG,
    host: request.Host,
    platform: request.Platform,
    market: request.Market,
    version: request.Version,
    namespace: request.Namespace ? request.Namespace.toLowerCase() : '',
    LCID: request.LCID,
    environment: environment,
  };
};

export const getEmeraldConfigSuccess = (response: EmeraldConfigResponseModel, environment: Environment, sourceApp: SourceApps) => {
  return {
    type: actionTypes.GET_EMERALD_CONFIG_SUCCESS,
    namespace: response.Namespace,
    navBarSettings: response.NavigationBarSettings,
    market: response.Market,
    appVer: response.AppVer,
    version: response.Version,
    supportSettingsConfig: response.SupportSettingsConfig,
    environment: environment,
    sourceApp: sourceApp,
  };
};

export const getEmeraldConfigError = (_response: boolean) => {
  return {
    type: actionTypes.GET_EMERALD_CONFIG_ERROR,
  };
};

export const getEmeraldSupportConfig = (request: EmeraldConfigRequestModel, environment: Environment, appVer: string) => {
  return {
    type: actionTypes.GET_EMERALD_SUPPORTCONFIG,
    market: request.Market,
    environment: environment,
    appVer: appVer,
  };
};

export const getEmeraldSupportConfigSuccess = (response: EmeraldConfigResponseModel, environment: Environment, appVer: string, sourceApp: SourceApps) => {
  return {
    type: actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS,
    supportSettingsConfig: response.SupportSettingsConfig,
    environment: environment,
    appVer: appVer,
    sourceApp: sourceApp,
  };
};

export const getEmeraldSupportConfigError = (_response: boolean) => {
  return {
    type: actionTypes.GET_EMERALD_SUPPORTCONFIG_ERROR,
  };
};

export const updateGetHelpConfig = (config: ExperienceMetaData) => {
  return {
    type: actionTypes.UPDATE_GETHELP_CONFIG,
    payload: config,
  };
};

export const updateTheme = (theme: string) => {
  return {
    type: actionTypes.UPDATE_THEME,
    theme: theme,
  };
};

export const updateAccentColor = (color: string) => {
  return {
    type: actionTypes.UPDATE_COLOR,
    payload: color,
  };
};

export const updateDeeplink = (deeplink: string) => {
  return {
    type: actionTypes.UPDATE_DEEPLINK,
    payload: deeplink,
  };
};

export const articleOnClickHandler = (url: string) => {
  return {
    type: actionTypes.CONTACT_ARTICLE_CLICKED,
    articleUrl: url,
  };
};

export const setDisplayNamespace = (displayNamespace: string) => {
  return {
    type: actionTypes.SET_DISPLAY_NAMESPACE,
    displayNamespace: displayNamespace,
  };
};

export const setInitializationComplete = () => {
  return {
    type: actionTypes.SET_INITIALIZATION_COMPLETE,
  };
};

export const updateNSAndVerCookiesForAgavePane = (environment: Environment) => {
  if (environment === Environment.agavePane) {
    return {
      type: actionTypes.UPDATE_NS_AND_VERSION_COOKIES_FOR_AGAVE_PANE,
    };
  }

  return {
    type: actionTypes.NO_UPDATE_FOR_NS_AND_VERSION_COOKIES_FOR_AGAVE_PANE,
  };
};

export const setSessionData = (sessionData: ISessionData) => {
  return {
    type: actionTypes.SET_SESSION_DATA,
    sessionData,
  };
};

export const updateUITabViewInfo = (uITabViewInfo: IUITabViewInfo, requestCreated?: boolean) => {
  return {
    type: actionTypes.UPDATE_UITABVIEWINFO,
    uITabViewInfo: uITabViewInfo,
    requestCreated: !!requestCreated,
  };
};

export const popUITabViewStack = () => {
  return {
    type: actionTypes.POP_UITABVIEWSTACK,
  };
};

export const resetUITabViewStack = () => (
  {
    type: actionTypes.RESET_PAGE_STACK,
  }
);

export const dismissOutdatedDevicesAlert = (comp: OutdatedDevicesAlertEnabledComponent) => {
  return {
    type: actionTypes.DISMISS_OUTDATED_DEVICES_ALERT,
    callingComponent: comp,
  };
};

export const toggleOutdatedDevicesAlertExpand = (comp: OutdatedDevicesAlertEnabledComponent) => {
  return {
    type: actionTypes.TOGGLE_OUTDATED_DEVICES_ALERT_EXPAND,
    callingComponent: comp,
  };
};

export const dismissVsbUpsellAlert = (comp: VsbUpsellAlertEnabledComponent) => {
  return {
    type: actionTypes.DISMISS_VSB_UPSELL_ALERT,
    callingComponent: comp,
  };
};

export const setCTAClickedVsbUpsellAlert = () => {
  return {
    type: actionTypes.SET_CTA_CLICKED_VSB_UPSELL_ALERT,
  };
};

export const setVsbUpsellAlertActivated = () => {
  return {
    type: actionTypes.SET_VSB_UPSELL_ALERT_ACTIVATED,
  };
};

export const updateGetHelpSignInStatus = (action: string) => {
  switch (action) {
    case IdentityChangedAction.SignIn:
    case IdentityChangedAction.Update:
      const authCommand: AuthTokenRequest = buildAuthTokenRequest(GetAuthTokenPurpose.SignInValidation, EmeraldSigninType.SilentOnly);
      notifyParent(JSON.stringify(authCommand));
      return signInUserIntoClientApp();
    case IdentityChangedAction.SignOut:
      return {
        type: actionTypes.USER_SIGN_OUT,
      };
  }
};

export const updateEcsConfiguration = (ecsConfiguration: EcsConfigurationResponse) => {
  return {
    type: actionTypes.UPDATE_ECS_CONFIGURATION,
    payload: ecsConfiguration,
  };
};

export const resetContactUsFocus = (shouldResetFocus: boolean) => {
  return {
    type: actionTypes.RESET_CONTACT_US_FOCUS,
    payload: shouldResetFocus,
  };
};

export const setIsQueryChanged = (isQueryChanged: boolean) => {
  return {
    type: actionTypes.SET_QUERY_CHANGED,
    isQueryChanged,
  };
}

export const showCoachmark = () => {
  return {
    type: actionTypes.TOGGLE_COACHMARK,
    isFeedbackCoachmarkShowing: true,
  };
};

export const hideCoachmark = () => {
  return {
    type: actionTypes.TOGGLE_COACHMARK,
    isFeedbackCoachmarkShowing: false,
  };
};
